import { useState, useRef, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX, FiExternalLink, FiDownload } from 'react-icons/fi';
import PressReleaseForm from './PressReleaseForm';
import { pressReleaseService } from '../services/apiService';

const PressReleaseList = ({ releases = [], fetchReleases }) => {
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const panelRef = useRef(null);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this press release?')) {
      setIsDeleting(true);
      try {
        await pressReleaseService.deletePressRelease(id);
        fetchReleases();
      } catch (error) {
        console.error('Error deleting press release:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (showForm && panelRef.current) {
      setTimeout(() => {
        panelRef.current.style.transform = 'translateX(0)';
      }, 10);
    }
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'translateX(100%)';
      setTimeout(() => {
        setShowForm(false);
      }, 300);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Press Releases</h3>
        <button
          onClick={() => {
            setSelectedRelease(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          <FiPlus className="mr-1" /> Create Press Release
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thumbnail
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Summary
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Publish Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Links
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {releases.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No press releases found. Create your first press release!
                </td>
              </tr>
            ) : (
              releases.map((release) => (
                <tr key={release._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {release.thumbnail ? (
                      <img
                        src={`data:image/jpeg;base64,${release.thumbnail}`}
                        alt={release.title}
                        className="h-12 w-16 object-cover rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="h-12 w-16 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                        No img
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                      {release.title}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-md truncate">
                      {release.summary}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(release.publishDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      release.isPublished 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {release.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      {release.pdfUrl && (
                        <a 
                          href={release.pdfUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          title="Download PDF"
                        >
                          <FiDownload className="h-4 w-4" />
                        </a>
                      )}
                      {release.webUrl && (
                        <a 
                          href={release.webUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          title="View Web Link"
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      {!release.pdfUrl && !release.webUrl && (
                        <span className="text-gray-400">No links</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedRelease(release);
                        setShowForm(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      <FiEdit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(release._id)}
                      disabled={isDeleting}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FiTrash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Slide Panel */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closePanel}></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div
                ref={panelRef}
                className="w-screen max-w-md transform transition-transform duration-300 ease-in-out translate-x-full"
              >
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-6 sm:px-6 bg-[#166534]">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-medium text-white">
                        {selectedRelease ? 'Edit Press Release' : 'Create Press Release'}
                      </h2>
                      <button onClick={closePanel} className="text-white hover:text-gray-200">
                        <FiX className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 py-6 px-4 sm:px-6">
                    <PressReleaseForm
                      release={selectedRelease}
                      onClose={closePanel}
                      onSave={() => {
                        fetchReleases();
                        closePanel();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PressReleaseList;
